/**
 * Timeline Audio Clip Component (canvas-v2)
 *
 * Audio clip block with purple styling.
 * Supports click to select, drag edges to trim, free-position drag (startOffset),
 * cross-track drag (trackIndex), and speed badge.
 * Volume/mute controls are NOT inline -- they live in AdjustPanel.
 */

import { useCallback, useState, useRef, memo } from 'react'
import type { TimelineAudioClip as TimelineAudioClipData } from './types'
import { pixelsToSeconds, secondsToPixels } from './timelineUtils'

interface TimelineAudioClipProps {
  /** Audio clip data */
  clip: TimelineAudioClipData

  /** Zoom level (pixels per second) */
  zoomLevel: number

  /** Whether this clip is selected */
  isSelected: boolean

  /** Callback when clip is clicked */
  onSelect: (clipId: string) => void

  /** Callback when clip is trimmed */
  onTrim: (clipId: string, trimStart: number, trimEnd: number) => void

  /** Callback when startOffset changes (free-position drag) */
  onOffsetChange: (clipId: string, startOffset: number) => void

  /** Callback when track index changes (cross-track drag) */
  onTrackIndexChange?: (clipId: string, newTrackIndex: number) => void

  /** Total number of audio tracks (for cross-track bounds) */
  audioTrackCount?: number

  /** Track height in pixels (for cross-track detection) */
  trackHeight?: number

  /** Whether timeline is locked (no editing) */
  locked?: boolean
}

export const TimelineAudioClipComponent = memo(function TimelineAudioClipComponent({
  clip,
  zoomLevel,
  isSelected,
  onSelect,
  onTrim,
  onOffsetChange,
  onTrackIndexChange,
  audioTrackCount = 1,
  trackHeight = 60,
  locked = false,
}: TimelineAudioClipProps) {
  const [isDragging, setIsDragging] = useState(false)
  const [dragType, setDragType] = useState<'move' | 'trim-start' | 'trim-end' | null>(null)
  const clipRef = useRef<HTMLDivElement>(null)
  const dragStartRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 })
  const startOffsetAtDragStart = useRef(clip.startOffset)
  const trackIndexAtDragStart = useRef(clip.trackIndex)

  // Calculate effective duration (original - trim start - trim end)
  const effectiveDuration = clip.originalDuration - clip.trimStart - clip.trimEnd
  const width = secondsToPixels(effectiveDuration, zoomLevel)
  const EDGE_RESIZE_WIDTH = 8

  const handleMouseDown = useCallback(
    (e: React.MouseEvent, type: 'move' | 'trim-start' | 'trim-end') => {
      if (locked) return

      e.preventDefault()
      e.stopPropagation()

      setIsDragging(true)
      setDragType(type)
      dragStartRef.current = { x: e.clientX, y: e.clientY }
      startOffsetAtDragStart.current = clip.startOffset
      trackIndexAtDragStart.current = clip.trackIndex

      if (type === 'move') {
        onSelect(clip.id)
      }

      const handleMouseMove = (moveEvent: MouseEvent) => {
        const deltaX = moveEvent.clientX - dragStartRef.current.x
        const deltaY = moveEvent.clientY - dragStartRef.current.y

        if (type === 'move') {
          // Free-position drag: update startOffset
          const deltaSec = pixelsToSeconds(deltaX, zoomLevel)
          const newOffset = Math.max(0, startOffsetAtDragStart.current + deltaSec)
          onOffsetChange(clip.id, newOffset)

          // Cross-track drag: detect vertical movement
          if (onTrackIndexChange && audioTrackCount > 1) {
            const trackDelta = Math.round(deltaY / trackHeight)
            const newTrackIndex = Math.max(
              0,
              Math.min(audioTrackCount - 1, trackIndexAtDragStart.current + trackDelta)
            )
            if (newTrackIndex !== clip.trackIndex) {
              onTrackIndexChange(clip.id, newTrackIndex)
            }
          }
        } else if (type === 'trim-start') {
          const deltaSec = pixelsToSeconds(deltaX, zoomLevel)
          const newTrimStart = Math.max(
            0,
            Math.min(clip.trimStart + deltaSec, clip.originalDuration - clip.trimEnd - 0.1)
          )
          onTrim(clip.id, newTrimStart, clip.trimEnd)
          dragStartRef.current = { x: moveEvent.clientX, y: moveEvent.clientY }
        } else if (type === 'trim-end') {
          const deltaSec = pixelsToSeconds(-deltaX, zoomLevel)
          const newTrimEnd = Math.max(
            0,
            Math.min(clip.trimEnd + deltaSec, clip.originalDuration - clip.trimStart - 0.1)
          )
          onTrim(clip.id, clip.trimStart, newTrimEnd)
          dragStartRef.current = { x: moveEvent.clientX, y: moveEvent.clientY }
        }
      }

      const handleMouseUp = () => {
        setIsDragging(false)
        setDragType(null)

        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }

      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    },
    [locked, clip, zoomLevel, onSelect, onTrim, onOffsetChange, onTrackIndexChange, audioTrackCount, trackHeight]
  )

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (!locked && !isDragging) {
        e.stopPropagation()
        onSelect(clip.id)
      }
    },
    [locked, isDragging, clip.id, onSelect]
  )

  const handleEdgeMouseDown = useCallback(
    (e: React.MouseEvent, edge: 'start' | 'end') => {
      e.stopPropagation()
      handleMouseDown(e, edge === 'start' ? 'trim-start' : 'trim-end')
    },
    [handleMouseDown]
  )

  return (
    <div
      ref={clipRef}
      className={`
        absolute h-full
        bg-purple-700/40 border border-purple-500
        rounded
        overflow-visible
        ${isSelected ? 'ring-2 ring-blue-400' : ''}
        ${isDragging && dragType === 'move' ? 'cursor-grabbing' : 'cursor-grab'}
        ${locked ? 'cursor-not-allowed' : ''}
      `}
      style={{
        width: `${width}px`,
        left: `${secondsToPixels(clip.startOffset, zoomLevel)}px`,
      }}
      onMouseDown={(e) => !locked && handleMouseDown(e, 'move')}
      onClick={handleClick}
    >
      {/* Clip content */}
      <div className="h-full flex items-center justify-between px-2 py-1">
        {/* Clip info */}
        <div className="flex-1 min-w-0">
          <div className="text-foreground text-xs font-medium truncate">
            {clip.sourcePath.split('/').pop() || 'Audio'}
          </div>
          <div className="text-text-secondary text-xs">
            {formatDuration(effectiveDuration)}
          </div>
        </div>

        {/* Speed badge + muted indicator */}
        <div className="flex items-center gap-1 ml-1">
          {clip.muted && (
            <span className="text-xs px-1 py-0.5 rounded bg-gray-500/60 text-gray-300">M</span>
          )}
          {clip.speed !== 1.0 && (
            <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-amber-500/80 text-white">
              {clip.speed}x
            </span>
          )}
        </div>
      </div>

      {/* Left edge resize handle */}
      {!locked && (
        <div
          className="absolute left-0 top-0 h-full w-2 cursor-ew-resize hover:bg-blue-400/30"
          style={{ width: `${EDGE_RESIZE_WIDTH}px` }}
          onMouseDown={(e) => handleEdgeMouseDown(e, 'start')}
        />
      )}

      {/* Right edge resize handle */}
      {!locked && (
        <div
          className="absolute right-0 top-0 h-full w-2 cursor-ew-resize hover:bg-blue-400/30"
          style={{ width: `${EDGE_RESIZE_WIDTH}px` }}
          onMouseDown={(e) => handleEdgeMouseDown(e, 'end')}
        />
      )}
    </div>
  )
})

function formatDuration(seconds: number): string {
  if (seconds < 60) {
    return `${seconds.toFixed(1)}s`
  }
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}
